import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Building2, Store, MapPin, Phone, User } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function OpsLocations({ user }) {
  const [locations, setLocations] = useState({ kitchen: null, outlets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.user_id) {
      fetchLocations();
    }
  }, [user]);

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/my-locations/${user.user_id}`, { 
        credentials: "include" 
      });
      if (res.ok) {
        setLocations(await res.json());
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!locations.kitchen) {
    return (
      <Card data-testid="ops-locations">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Kitchen Assigned</h3>
            <p className="text-muted-foreground">Contact your administrator to assign you a kitchen.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ops-locations">
      {/* Kitchen Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            My Kitchen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-primary/10 rounded-lg p-4">
            <h3 className="font-semibold text-lg">{locations.kitchen.name}</h3>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {locations.kitchen.address}
              </div>
              {locations.kitchen.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {locations.kitchen.phone}
                </div>
              )}
              {locations.kitchen.manager_name && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  {locations.kitchen.manager_name}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outlets Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            My Outlets ({locations.outlets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {locations.outlets.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No outlets under this kitchen</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.outlets.map((outlet) => (
                <div key={outlet.outlet_id} className="border rounded-lg p-4 hover:border-primary transition-colors">
                  <h4 className="font-medium">{outlet.name}</h4>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      {outlet.address}
                    </div>
                    {outlet.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3" />
                        {outlet.phone}
                      </div>
                    )}
                    {outlet.contact_person && (
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        {outlet.contact_person}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
